import { type FC, type ReactNode, useMemo } from "react";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/Chart";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Card } from "@/components/podkit/Card";
import { Text } from "@/components/podkit/typography/Text";
import { OCU } from "@/routes/settings/organization/billing/OCU";

type ForecastDataPoint = {
    date: string;
    remaining: number;
};

type Props = {
    availableCredits: number;
    avgDailyBurn: number;
    /** Optional action element rendered at the top-right of the card header. */
    headerAction?: ReactNode;
};

const FORECAST_COLOR = "#8B5CF6";

function formatFutureDate(daysFromNow: number): string {
    const d = new Date();
    d.setDate(d.getDate() + daysFromNow);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export const ForecastChart: FC<Props> = ({ availableCredits, avgDailyBurn, headerAction }) => {
    const forecastData = useMemo((): ForecastDataPoint[] => {
        if (avgDailyBurn <= 0) {
            // No burn rate — just show a flat line at current credits for 30 days
            return [
                { date: formatFutureDate(0), remaining: availableCredits },
                { date: formatFutureDate(30), remaining: availableCredits },
            ];
        }

        const points: ForecastDataPoint[] = [];
        let remaining = availableCredits;
        let day = 0;

        while (remaining > 0) {
            points.push({ date: formatFutureDate(day), remaining: Math.round(remaining) });
            remaining -= avgDailyBurn;
            day++;
        }

        // Final point at 0
        points.push({ date: formatFutureDate(day), remaining: 0 });

        return points;
    }, [availableCredits, avgDailyBurn]);

    const daysUntilZero = avgDailyBurn > 0 ? Math.ceil(availableCredits / avgDailyBurn) : undefined;

    const chartConfig = {
        remaining: {
            label: "Remaining credits",
            color: FORECAST_COLOR,
        },
    } satisfies ChartConfig;

    return (
        <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <Text className="text-lg font-semibold text-content-primary">Credit Burndown</Text>
                    <Text className="text-sm text-content-secondary">
                        {daysUntilZero !== undefined ? (
                            <>
                                {"At current usage ("}
                                {Math.round(avgDailyBurn).toLocaleString()} <OCU />
                                {"/day), credits will"}
                                run out in {daysUntilZero} {daysUntilZero === 1 ? "day" : "days"}
                            </>
                        ) : (
                            "No usage detected — credits will not deplete"
                        )}
                    </Text>
                </div>
                {headerAction}
            </div>

            <ChartContainer config={chartConfig} className="h-[250px] w-full">
                <AreaChart data={forecastData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                        <linearGradient id="fill-forecast" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={FORECAST_COLOR} stopOpacity={0.35} />
                            <stop offset="95%" stopColor={FORECAST_COLOR} stopOpacity={0.06} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} minTickGap={32} />
                    <YAxis
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                        tickFormatter={(value: number) =>
                            value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toString()
                        }
                    />
                    <ChartTooltip
                        content={
                            <ChartTooltipContent
                                valueFormatter={(value) =>
                                    `${Math.round(Number(value)).toLocaleString()} OCU`
                                }
                            />
                        }
                    />
                    <Area
                        type="linear"
                        dataKey="remaining"
                        stroke={FORECAST_COLOR}
                        fill="url(#fill-forecast)"
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={false}
                    />
                </AreaChart>
            </ChartContainer>
        </Card>
    );
};
